import React, { useState, useEffect } from 'react';
import DailyBalancingAndroid from './DailyBalancingAndroid';
import HourlyReportingAndroid from './HourlyReportingAndroid';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

const C = {
  acc: '#185FA5', accBg: '#E6F1FB', accT: '#0C447C',
  grn: '#3B6D11', grnBg: '#EAF3DE',
  red: '#A32D2D', redBg: '#FCEBEB',
  border: 'rgba(0,0,0,0.10)',
  bg1: '#ffffff', bg2: '#F7F6F3', bg3: '#EFEDE8',
  txt: '#1a1a1a', txt2: '#666666'
};

// ─── Mobile Login ─────────────────────────────────────────────────────────────
function MobileLogin({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    const emailEl = document.getElementById('android-email');
    const passwordEl = document.getElementById('android-password');
    const email = emailEl ? emailEl.value.trim() : '';
    const password = passwordEl ? passwordEl.value : '';

    if (!email || !password) {
      setError(`Email: "${email}" | Pass length: ${password.length}`);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const r = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      const data = await r.json();
      if (!r.ok) { setError(data.error || 'Login failed'); setLoading(false); return; }
      localStorage.setItem('lc_token', data.token);
      localStorage.setItem('lc_user', JSON.stringify(data.user));
      onLogin(data.user);
    } catch (err) {
      setError(`Fetch error: ${err.message}`);
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: C.bg3,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ textAlign: 'center', marginBottom: 40 }}>
        <div style={{ fontSize: 32, fontWeight: 800, color: C.acc }}>Skillflo</div>
        <div style={{ fontSize: 14, color: C.txt2, marginTop: 6 }}>Skill Deployment & Line Balancing</div>
      </div>

      <div style={{
        width: '100%', maxWidth: 360, background: C.bg1,
        borderRadius: 16, padding: 24,
        boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Sign in</div>

        {error && (
          <div style={{
            background: C.redBg, border: `1px solid ${C.red}`,
            borderRadius: 8, padding: '10px 14px', marginBottom: 16,
            fontSize: 12, color: C.red, wordBreak: 'break-all'
          }}>{error}</div>
        )}

        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.txt2, marginBottom: 6 }}>Email</div>
          <input
            id="android-email"
            type="text"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck="false"
            placeholder="your@email.com"
            style={{
              width: '100%', padding: '13px 14px', borderRadius: 10,
              border: `1.5px solid ${C.border}`, fontSize: 15,
              background: '#fff', color: C.txt, boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.txt2, marginBottom: 6 }}>Password</div>
          <input
            id="android-password"
            type="password"
            placeholder="••••••••"
            style={{
              width: '100%', padding: '13px 14px', borderRadius: 10,
              border: `1.5px solid ${C.border}`, fontSize: 15,
              background: '#fff', color: C.txt, boxSizing: 'border-box'
            }}
          />
        </div>

        <button onClick={handleLogin} disabled={loading} style={{
          width: '100%', padding: '14px 0', borderRadius: 10, border: 'none',
          background: loading ? C.accBg : C.acc,
          color: loading ? C.acc : '#fff',
          fontSize: 16, fontWeight: 700, cursor: 'pointer'
        }}>
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </div>
    </div>
  );
}

// ─── Landing Screen ───────────────────────────────────────────────────────────
function LandingScreen({ onSelect, onLogout, user }) {
  return (
    <div style={{
      minHeight: '100vh', background: C.bg3,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      padding: '32px 24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      <div style={{ textAlign: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: C.acc }}>Skillflo</div>
        <div style={{ fontSize: 13, color: C.txt2, marginTop: 4 }}>
          Welcome, {user?.name || 'User'}
        </div>
      </div>

      <div style={{ width: '100%', maxWidth: 360, marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16 }}>

        <div onClick={() => onSelect('balancing')} style={{
          background: C.bg1, borderRadius: 16, padding: '28px 24px',
          border: `2px solid ${C.border}`, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: C.accBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke={C.acc} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" />
              <rect x="14" y="3" width="7" height="7" />
              <rect x="3" y="14" width="7" height="7" />
              <rect x="14" y="14" width="7" height="7" />
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: C.txt }}>Daily Balancing</div>
            <div style={{ fontSize: 13, color: C.txt2, marginTop: 3 }}>Assign operators to operations for today</div>
          </div>
        </div>

        <div onClick={() => onSelect('hourly')} style={{
          background: C.bg1, borderRadius: 16, padding: '28px 24px',
          border: `2px solid ${C.border}`, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 20,
          boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: 14, background: C.grnBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke={C.grn} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 17, color: C.txt }}>Hourly Reporting</div>
            <div style={{ fontSize: 13, color: C.txt2, marginTop: 3 }}>Record production, cycle time and downtime</div>
          </div>
        </div>

      </div>

      <button onClick={onLogout} style={{
        marginTop: 40, padding: '10px 24px', borderRadius: 8,
        border: `1px solid ${C.border}`, background: 'transparent',
        fontSize: 13, color: C.txt2, cursor: 'pointer'
      }}>Sign out</button>
    </div>
  );
}

// ─── Main Shell ───────────────────────────────────────────────────────────────
export default function AndroidShell() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [activeTab, setActiveTab] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('lc_token');
    const storedUser = localStorage.getItem('lc_user');
    if (token && storedUser) {
      try { setUser(JSON.parse(storedUser)); }
      catch (e) {
        localStorage.removeItem('lc_token');
        localStorage.removeItem('lc_user');
      }
    }
    setChecking(false);
  }, []);

  const handleLogin = (u) => setUser(u);

  const handleLogout = () => {
    localStorage.removeItem('lc_token');
    localStorage.removeItem('lc_user');
    setUser(null);
    setActiveTab(null);
  };

  if (checking) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: C.bg3, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif'
      }}>
        <div style={{ fontSize: 14, color: C.txt2 }}>Loading…</div>
      </div>
    );
  }

  if (!user) return <MobileLogin onLogin={handleLogin} />;

  if (activeTab === null) {
    return <LandingScreen onSelect={setActiveTab} onLogout={handleLogout} user={user} />;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden',
      fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: C.bg2
    }}>
      <div style={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
        <div style={{
          position: 'absolute', inset: 0, overflowY: 'auto',
          display: activeTab === 'balancing' ? 'block' : 'none'
        }}>
          <DailyBalancingAndroid />
        </div>
        <div style={{
          position: 'absolute', inset: 0, overflowY: 'auto',
          display: activeTab === 'hourly' ? 'block' : 'none'
        }}>
          <HourlyReportingAndroid />
        </div>
      </div>

      <div style={{
        display: 'flex', flexShrink: 0, background: C.bg1,
        borderTop: `1px solid ${C.border}`,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}>
        <button onClick={() => setActiveTab(null)} style={{
          width: 56, padding: '10px 0 12px', border: 'none', cursor: 'pointer',
          background: 'transparent', borderTop: '3px solid transparent',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={C.txt2} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            <polyline points="9 22 9 12 15 12 15 22" />
          </svg>
          <span style={{ fontSize: 9, color: C.txt2, fontWeight: 500 }}>Home</span>
        </button>

        <div style={{ width: 1, background: C.border, margin: '8px 0' }} />

        <button onClick={() => setActiveTab('balancing')} style={{
          flex: 1, padding: '10px 0 12px', border: 'none', cursor: 'pointer',
          background: 'transparent',
          borderTop: `3px solid ${activeTab === 'balancing' ? C.acc : 'transparent'}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={activeTab === 'balancing' ? C.acc : C.txt2} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
          </svg>
          <span style={{
            fontSize: 11, fontWeight: activeTab === 'balancing' ? 700 : 500,
            color: activeTab === 'balancing' ? C.acc : C.txt2
          }}>Daily Balancing</span>
        </button>

        <div style={{ width: 1, background: C.border, margin: '8px 0' }} />

        <button onClick={() => setActiveTab('hourly')} style={{
          flex: 1, padding: '10px 0 12px', border: 'none', cursor: 'pointer',
          background: 'transparent',
          borderTop: `3px solid ${activeTab === 'hourly' ? C.grn : 'transparent'}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3
        }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={activeTab === 'hourly' ? C.grn : C.txt2} strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span style={{
            fontSize: 11, fontWeight: activeTab === 'hourly' ? 700 : 500,
            color: activeTab === 'hourly' ? C.grn : C.txt2
          }}>Hourly Reporting</span>
        </button>
      </div>
    </div>
  );
}